import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {DetailsTabContentWrapper} from '@/features/projects/components/details-tab-content-wrapper/DetailsTabContentWrapper'
import {useTranslation} from '@/translations/i18n'
import {useMemo, useState} from 'react'
import {useEditProjectEvaluationScales} from '@/features/projects/queries/useEditProjectEvaluationScale'
import {useOutletContext} from 'react-router-dom'
import {EvaluationScaleExtended, ProjectExtended} from '@/features/projects/types'
import {errorHandler} from '@/utilities/helpers'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {EvaluationRow} from '@/features/projects/components/evaulation-row/EvaluationRow'
import {StoreEditEvaluation} from '@/features/projects/components/store-evaluation/StoreEvaluation'
import {Text} from '@/components/ui/text/Text'
import {Helmet} from 'react-helmet'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import dayjs from 'dayjs'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {useGetEvents} from '@/features/agenda/queries/useGetEvents'

export const ProjectDetailsScalesTests = () => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()
    const events = useGetEvents({
        params: {
            projectId: project.id
        },
        options: {
            enabled: !!project.id
        }
    })

    const [isEditMode, setIsEditMode] = useState(false)
    const [temporaryEvaluations, setTemporaryEvaluations] = useState<EvaluationScaleExtended[]>(
        project.evaluationScales
    )
    const confirmMutation = useEditProjectEvaluationScales({
        urlParams: {projectId: project.id},
        options: {
            onError: errorHandler,
            onSuccess: (_, variables) => {
                queryClient.setQueriesData<ProjectExtended | undefined>(
                    {queryKey: [QUERY_KEYS.PROJECT, project.id]},
                    old => {
                        if (!old) {
                            return old
                        }
                        return {
                            ...old,
                            evaluationScales: variables
                        }
                    }
                )
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
                setIsEditMode(false)
            }
        }
    })

    const completedOpeningEvent = useMemo(
        () =>
            events.data?.find(
                event =>
                    event.status == 'completed' &&
                    (event.eventConsultants?.[0]?.accessType?.accessType == 'project_opening_processing' ||
                        event.eventConsultants?.[0]?.accessType?.accessType == 'cycle_opening_processing')
            ),
        [events.data]
    )

    return (
        <UnsavedBlocker isDirty={isEditMode}>
            <DetailsTabContentWrapper>
                <Helmet title={t('commons:fullname_details_meta_title', {fullName: project.patient.fullName})} />
                <DetailsFieldsSection
                    variant="neutral"
                    title={t('projects:tab_scales_and_tests')}
                    headingEndSlot={
                        isEditable && (
                            <EditDetailsSectionButtons
                                isLoading={confirmMutation.isPending}
                                isActive={isEditMode}
                                isDisabled={isEditMode && temporaryEvaluations.length < 2}
                                onEdit={() => setIsEditMode(true)}
                                onCancel={() => {
                                    setIsEditMode(false)
                                    setTemporaryEvaluations(project.evaluationScales)
                                }}
                                onConfirm={() =>
                                    confirmMutation.mutate(
                                        temporaryEvaluations.map(evaluationScale => ({
                                            ...evaluationScale,
                                            evaluationDate: dayjs(evaluationScale.evaluationDate).format('YYYY-MM-DD')
                                        }))
                                    )
                                }
                            />
                        )
                    }
                >
                    {isEditMode && temporaryEvaluations.length == 0 ? (
                        <Flexbox direction="column" gap={6}>
                            <Flexbox direction="column" gap={2}>
                                <Text palette="neutral" shade="600" size="textMd" weight="500">
                                    {t('projects:no_evaluation_scale_title')}
                                </Text>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('projects:no_evaluation_scale_description')}
                                </Text>
                            </Flexbox>
                            <StoreEditEvaluation
                                temporaryEvaluations={temporaryEvaluations}
                                openingEventDate={completedOpeningEvent?.date}
                                onSubmit={evaluationScale => {
                                    setTemporaryEvaluations([...temporaryEvaluations, evaluationScale])
                                }}
                            />
                        </Flexbox>
                    ) : (
                        <Flexbox direction="column" gap={4} fullWidth>
                            {temporaryEvaluations.length == 0 ? (
                                <EvaluationRow isPlaceholder />
                            ) : (
                                temporaryEvaluations.map((evaluationScale, index) => (
                                    <EvaluationRow
                                        key={index}
                                        evaluationScale={evaluationScale}
                                        index={index}
                                        isEditMode={isEditMode}
                                        temporaryEvaluations={temporaryEvaluations}
                                        openingEventDate={completedOpeningEvent?.date}
                                        onDelete={() => {
                                            setTemporaryEvaluations(
                                                temporaryEvaluations.filter(
                                                    (_, temporaryEvaluationIndex) => temporaryEvaluationIndex != index
                                                )
                                            )
                                        }}
                                        onEdit={evaluationScale => {
                                            setTemporaryEvaluations(
                                                temporaryEvaluations.map((_, temporaryEvaluationIndex) =>
                                                    temporaryEvaluationIndex == index ? evaluationScale : _
                                                )
                                            )
                                        }}
                                    />
                                ))
                            )}
                            {isEditMode && (
                                <StoreEditEvaluation
                                    openingEventDate={completedOpeningEvent?.date}
                                    temporaryEvaluations={temporaryEvaluations}
                                    onSubmit={evaluationScale => {
                                        setTemporaryEvaluations([...temporaryEvaluations, evaluationScale])
                                    }}
                                />
                            )}
                        </Flexbox>
                    )}
                </DetailsFieldsSection>
            </DetailsTabContentWrapper>
        </UnsavedBlocker>
    )
}
